import { cn } from '@up2stay/up2promo-ui';
import { useEffect, useState } from 'react';

export type SetupSectionId =
  | 'connections'
  | 'analysis'
  | 'responses'
  | 'alerts';

export const SETUP_SECTION_IDS: SetupSectionId[] = [
  'connections',
  'analysis',
  'responses',
  'alerts',
];

/** Scroll a setup section into view (anchors are the section wrappers' ids). */
export function scrollToSetupSection(id: SetupSectionId) {
  document
    .getElementById(`setup-${id}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Sticky in-page anchor nav; highlights the section nearest the top of the viewport. */
export function ReputationSetupNav({
  labels,
  className,
}: {
  labels: Record<SetupSectionId, string>;
  className?: string;
}) {
  const [active, setActive] = useState<SetupSectionId>('connections');

  useEffect(() => {
    const elements = SETUP_SECTION_IDS.map((id) =>
      document.getElementById(`setup-${id}`),
    ).filter((el): el is HTMLElement => el != null);
    if (elements.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0]?.target.id.replace('setup-', '');
        if (top) setActive(top as SetupSectionId);
      },
      { rootMargin: '-10% 0px -70% 0px' },
    );
    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <nav
      aria-label="Setup"
      className={cn('flex flex-col gap-0.5 pt-1 text-[13px]', className)}
    >
      {SETUP_SECTION_IDS.map((id) => (
        <button
          key={id}
          type="button"
          aria-current={active === id ? 'true' : undefined}
          onClick={() => {
            setActive(id);
            scrollToSetupSection(id);
          }}
          className={cn(
            'rounded-[9px] border px-3 py-2 text-left transition-colors',
            active === id
              ? 'border-[color:var(--nav-active-border)] bg-[color:var(--nav-active-bg)] font-semibold text-[color:var(--nav-active-fg)]'
              : 'border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground',
          )}
        >
          {labels[id]}
        </button>
      ))}
    </nav>
  );
}
